// src/components/dashboard/DraftReplyPanel.tsx
import { useRef, useEffect, useState, type ChangeEvent } from 'react';
import type { BookingJob } from '@/lib/types/workflow';
import { formatRand } from '@/lib/utils/currency';
import { brand, fonts, motion, styles } from '@/lib/tokens';
import { ExpandOverlay } from '@/components/ui/ExpandOverlay';

type Props = {
  booking: BookingJob;
  draft: string;
  onDraftChange: (next: string) => void;
  editable?: boolean;
  showToolbar?: boolean;
  /** Operator-confirmed CPT/CPT-Local price tier switch — see
   *  [[finding-cpt-jhb-tier-pricing-doc-gap]]. Only rendered when provided,
   *  and QuoteSummary itself gates visibility on the booking being a
   *  Cape Town booking with a saved draft. */
  onReprice?: (tier: 'CPT' | 'CPT Local') => void | Promise<void>;
  isRepricing?: boolean;
};

export function DraftReplyPanel({
  booking,
  draft,
  onDraftChange,
  editable = false,
  showToolbar = false,
  onReprice,
  isRepricing = false,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <section
        className="flex flex-col overflow-hidden panel-col"
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Panel header */}
        <div
          className="panel-header"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>Draft Reply</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {editable && (
              <span style={{ ...styles.monoLabel, letterSpacing: '0.1em' }}>
                Draft Mode
              </span>
            )}
            <button
              onClick={() => setExpanded(true)}
              title="Expand editor"
              aria-label="Expand draft reply"
              className="expand-trigger"
              style={{
                padding: '6px',
                minWidth: 32,
                minHeight: 32,
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: brand.monoDim,
                transition: `color ${motion.fast}`,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = brand.onSurface;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = brand.monoDim;
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                open_in_full
              </span>
            </button>
          </div>
        </div>

        {/* Scrollable content — panel scrolls, card grows naturally */}
        <div
          className="custom-scroll"
          style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column' }}
        >
          <DraftEditorContent
            booking={booking}
            draft={draft}
            onDraftChange={onDraftChange}
            editable={editable}
            showToolbar={showToolbar}
            expanded={false}
            onReprice={onReprice}
            isRepricing={isRepricing}
          />
        </div>
      </section>

      {/* Overlay — textarea fills the full content area */}
      <ExpandOverlay
        open={expanded}
        title="Draft Reply"
        subtitle={`${booking.clientName} · Booking ${booking.id}`}
        onClose={() => setExpanded(false)}
        contentFill
      >
        <DraftEditorContent
          booking={booking}
          draft={draft}
          onDraftChange={onDraftChange}
          editable={editable}
          showToolbar={showToolbar}
          expanded={true}
        />
      </ExpandOverlay>
    </>
  );
}

// ---------------------------------------------------------------------------
// DraftEditorContent — handles both column (auto-grow) and overlay (fill) modes
// ---------------------------------------------------------------------------

type DraftEditorContentProps = {
  booking: BookingJob;
  draft: string;
  onDraftChange: (next: string) => void;
  editable: boolean;
  showToolbar: boolean;
  expanded: boolean;
  onReprice?: (tier: 'CPT' | 'CPT Local') => void | Promise<void>;
  isRepricing?: boolean;
};

function DraftEditorContent({
  booking,
  draft,
  onDraftChange,
  editable,
  showToolbar,
  expanded,
  onReprice,
  isRepricing = false,
}: DraftEditorContentProps) {
  const innerRef = useRef<HTMLTextAreaElement>(null);

  // Focus cursor at end when editable turns on
  useEffect(() => {
    if (!editable || !innerRef.current) return;
    innerRef.current.focus();
    const len = innerRef.current.value.length;
    innerRef.current.setSelectionRange(len, len);
  }, [editable]);

  // Auto-grow in column mode: re-measure whenever the draft value changes
  useEffect(() => {
    if (expanded || !innerRef.current) return;
    innerRef.current.style.height = 'auto';
    innerRef.current.style.height = innerRef.current.scrollHeight + 'px';
  }, [draft, expanded]);

  const wordCount = draft.trim() ? draft.trim().split(/\s+/).length : 0;

  // ── OVERLAY MODE ──────────────────────────────────────────────────────────
  if (expanded) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {/* Unified writing surface: toolbar + textarea as one visual block */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: 'rgba(var(--fg-rgb),0.02)',
            border: '1px solid rgba(var(--fg-rgb),0.06)',
            borderRadius: 12,
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {/* Overlay toolbar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 16px',
              borderBottom: '1px solid rgba(var(--fg-rgb),0.05)',
              background: 'rgba(var(--fg-rgb),0.02)',
              borderRadius: '12px 12px 0 0',
              flexShrink: 0,
              gap: 8,
            }}
          >
            <ToolbarBtn icon="format_bold" />
            <ToolbarBtn icon="format_italic" />
            <ToolbarBtn icon="link" />
            <div style={{ flex: 1 }} />
            <span
              style={{
                fontFamily: fonts.mono,
                fontSize: 10,
                color: brand.monoDim,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {wordCount} {wordCount === 1 ? 'word' : 'words'}
            </span>
          </div>

          {/* Full-height writing area */}
          {editable ? (
            <textarea
              ref={innerRef}
              autoFocus
              className="custom-scroll"
              value={draft}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onDraftChange(e.target.value)}
              placeholder="Write your reply…"
              style={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                padding: 24,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                resize: 'none',
                fontFamily: fonts.serif,
                fontSize: 15,
                color: 'rgba(var(--fg-rgb),0.85)',
                lineHeight: 1.8,
                overflowY: 'auto',
              }}
            />
          ) : (
            <div
              className="custom-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                padding: 24,
                overflowY: 'auto',
                fontFamily: fonts.serif,
                fontSize: 15,
                color: 'rgba(var(--fg-rgb),0.85)',
                lineHeight: 1.8,
                overflowWrap: 'break-word',
              }}
            >
              {draft.split(/\n\n+/).map((p, i) => (
                <p key={i} style={{ marginTop: 0, marginBottom: 12, whiteSpace: 'pre-wrap' }}>
                  {p}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── COLUMN MODE ───────────────────────────────────────────────────────────
  const showExtractionWarning =
    !!booking.draftId && (booking.aiConfidence === 0 || booking.gear.length === 0);

  return (
    <>
      {!booking.draftBody ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 12,
            padding: 24,
          }}
        >
          <span
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'rgba(var(--fg-rgb),0.3)',
              textAlign: 'center',
            }}
          >
            {booking.backendStatus === 'RECEIVED' && 'AWAITING AI EXTRACTION'}
            {booking.backendStatus === 'PROCESSING' && 'AI IS EXTRACTING GEAR...'}
            {booking.backendStatus === 'FAILED' && 'PROCESSING FAILED'}
            {!booking.backendStatus && 'NO DRAFT AVAILABLE'}
          </span>
        </div>
      ) : (
        <>
          {showExtractionWarning && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 12px',
                marginBottom: 12,
                background: 'rgba(245,158,11,0.08)',
                border: '1px solid rgba(245,158,11,0.25)',
                borderRadius: 8,
                flexShrink: 0,
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 16, color: '#f59e0b', flexShrink: 0, marginTop: 1 }}
              >
                warning
              </span>
              <span
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 11,
                  color: '#f59e0b',
                  lineHeight: 1.5,
                }}
              >
                AI extraction returned no results — please review the email and complete the draft manually
              </span>
            </div>
          )}

          {/* Draft email card — height driven entirely by content */}
          <div
            className="card-border"
            style={{
              background: brand.surface4,
              padding: 16,
              borderRadius: 12,
              position: 'relative',
              flexShrink: 0,
            }}
          >
            {/* Edit icon top-right */}
            <span
              className="material-symbols-outlined"
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                fontSize: 16,
                color: brand.monoDim,
              }}
            >
              edit
            </span>

            {editable ? (
              <>
                {showToolbar && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      padding: '8px 12px',
                      borderBottom: `1px solid ${brand.hairlineSoft}`,
                      background: brand.hairlineSoft,
                      borderRadius: '8px 8px 0 0',
                      margin: '-16px -16px 12px',
                    }}
                  >
                    <ToolbarBtn icon="format_bold" />
                    <ToolbarBtn icon="format_italic" />
                    <ToolbarBtn icon="link" />
                  </div>
                )}
                {/* Auto-growing textarea: height set by JS, parent card grows to fit */}
                <textarea
                  ref={innerRef}
                  value={draft}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onDraftChange(e.target.value)}
                  onInput={(e) => {
                    const el = e.currentTarget;
                    el.style.height = 'auto';
                    el.style.height = el.scrollHeight + 'px';
                  }}
                  placeholder="Write your reply…"
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    minHeight: 120,
                    overflow: 'hidden',
                    resize: 'none',
                    fontFamily: fonts.sans,
                    fontSize: 14,
                    color: 'rgba(var(--fg-rgb),0.8)',
                    lineHeight: 1.7,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    padding: 0,
                  }}
                />
              </>
            ) : (
              <div
                style={{
                  fontFamily: fonts.sans,
                  fontSize: 14,
                  color: 'rgba(var(--fg-rgb),0.8)',
                  lineHeight: 1.7,
                  paddingRight: 24,
                  overflowWrap: 'break-word',
                  minHeight: 120,
                }}
              >
                {draft.split(/\n\n+/).map((p, i) => (
                  <p key={i} style={{ marginTop: 0, marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                    {p}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Quote Summary */}
          <div style={{ marginTop: 20 }}>
            <QuoteSummary booking={booking} onReprice={onReprice} isRepricing={isRepricing} />
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ToolbarBtn({ icon }: { icon: string }) {
  return (
    <button
      style={{
        background:     'transparent',
        border:         'none',
        color:          brand.onSurfaceVariant,
        cursor:         'pointer',
        padding:        4,
        borderRadius:   4,
        display:        'inline-flex',
        alignItems:     'center',
        justifyContent: 'center',
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
        {icon}
      </span>
    </button>
  );
}

// Mirrors draft-service's _CPT_LOCATION_VALUES guard — the toggle only makes
// sense (and the backend only accepts a reprice) for a Cape Town booking.
const _CPT_LOCATION_VALUES = new Set(['cape town', 'cpt']);

function QuoteSummary({
  booking,
  onReprice,
  isRepricing = false,
}: {
  booking: BookingJob;
  onReprice?: (tier: 'CPT' | 'CPT Local') => void | Promise<void>;
  isRepricing?: boolean;
}) {
  const rentalDays = Math.max(
    1,
    booking.rentalDates.match(/(\d+)\s*day/)?.[1]
      ? Number(booking.rentalDates.match(/(\d+)\s*day/)?.[1])
      : 1,
  );
  const subtotal = booking.gear.reduce(
    (s, g) => s + g.unitPrice * g.quantity * rentalDays,
    0,
  );
  const vat = subtotal * 0.15;
  const total = subtotal + vat;

  const isCptBooking = _CPT_LOCATION_VALUES.has(booking.location.trim().toLowerCase());
  const showTierToggle = isCptBooking && !!booking.draftId && !!onReprice;
  const activeTier = booking.priceTier ?? 'CPT';

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 8,
          padding: '0 4px',
        }}
      >
        <p style={{ ...styles.monoLabel, letterSpacing: '0.1em', margin: 0 }}>
          Quote Summary ({rentalDays} {rentalDays === 1 ? 'Day' : 'Days'})
        </p>

        {showTierToggle && (
          <div
            role="group"
            aria-label="Cape Town pricing tier"
            style={{
              display: 'inline-flex',
              borderRadius: 8,
              overflow: 'hidden',
              border: `1px solid ${brand.hairlineSoft}`,
              opacity: isRepricing ? 0.6 : 1,
              pointerEvents: isRepricing ? 'none' : 'auto',
            }}
          >
            {(['CPT', 'CPT Local'] as const).map((tier) => {
              const active = activeTier === tier;
              return (
                <button
                  key={tier}
                  type="button"
                  disabled={isRepricing}
                  onClick={() => {
                    if (!active) onReprice?.(tier);
                  }}
                  style={{
                    padding: '5px 10px',
                    fontFamily: fonts.mono,
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    border: 'none',
                    cursor: active ? 'default' : 'pointer',
                    background: active ? brand.accent : 'transparent',
                    color: active ? brand.surface1 : brand.onSurfaceVariant,
                    transition: `background ${motion.fast}, color ${motion.fast}`,
                  }}
                >
                  {isRepricing && active ? '…' : tier}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div
        className="card-border"
        style={{ borderRadius: 12, background: brand.surface1, overflow: 'hidden' }}
      >
        <table className="quote-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Item</th>
              <th style={{ textAlign: 'right' }}>Price</th>
            </tr>
          </thead>
          <tbody>
            {booking.gear.map((g) => (
              <tr key={g.id}>
                <td style={{ color: brand.onSurfaceVariant }}>{g.name}</td>
                <td style={{ textAlign: 'right', color: brand.onSurface }}>
                  {formatRand(g.unitPrice * g.quantity * rentalDays)}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ color: brand.monoDim }}>Subtotal</td>
              <td style={{ textAlign: 'right', color: brand.monoDim }}>{formatRand(subtotal)}</td>
            </tr>
            <tr>
              <td style={{ color: brand.monoDim }}>VAT (15%)</td>
              <td style={{ textAlign: 'right', color: brand.monoDim }}>{formatRand(vat)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>Total Estimation</td>
              <td style={{ textAlign: 'right', fontSize: 18 }}>{formatRand(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
